<template>
  <div class="me-journey" v-if="steps && steps.length">
    <div class="me-journey-head">
      <i :class="headIcon" :style="{ color: headColor }"></i>
      <span class="me-journey-title">{{ headTitle }}</span>
    </div>

    <div class="me-journey-track">
      <div
        class="me-jstep"
        v-for="(item, index) in steps"
        :key="index"
      >
        <div
          v-if="index > 0"
          class="me-jconnector"
          :style="{ background: connectorColor(index) }"
        ></div>
        <div class="me-jcircle" :style="circleStyle(item.state)">
          <i :class="iconForStatus(item.status)"></i>
        </div>
        <div class="me-jlabel" :style="{ color: labelColor(item.state) }">
          {{ item.title }}
        </div>
        <div class="me-jdate" v-if="dateFor(index, item)">
          {{ dateFor(index, item) }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { formatShortCalendarDate } from "@/utils/calendar-date.mjs";
import { journeyHeadStep } from "./journey-head";

// Cores da marca Mais Envios: azul #22418d (nav/menu) e laranja #F47920 (logo "+").
const COLORS = {
  done: "#22418d",    // concluído = azul da marca
  current: "#F47920", // etapa atual = laranja da marca
  warning: "#E6A23C",
  error: "#F25C5C",
  pendingBorder: "#C7CBD1",
  pendingText: "#9AA0A6",
  track: "#DCDFE6",
};

const MONTHS = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];

// Ícone Font Awesome 6 (solid) por STATUS — mantém o ícone do status em qualquer estado
// (done = o ícone do status em verde, não um check genérico).
const STATUS_ICON = {
  1: "fas fa-tag",                  // Pré-postagem
  2: "fas fa-hand-holding",         // Coletado
  8: "fas fa-warehouse",            // Tratado no CD
  9: "fas fa-route",                // Encaminhado UF
  3: "fas fa-box",                  // Postado
  4: "fas fa-truck",                // Em trânsito
  7: "fas fa-truck-fast",           // Saiu para entrega
  5: "fas fa-circle-check",         // Entregue
  11: "fas fa-clock",               // Aguardando retirada
  17: "fas fa-calendar-check",      // Entrega programada
  15: "fas fa-rotate-left",         // Devolução
  16: "fas fa-rotate-left",         // Entregue ao contratante (devolvido ao remetente)
  12: "fas fa-circle-exclamation",  // Entrega não efetuada
  14: "fas fa-magnifying-glass",    // Averiguar falha na entrega
  13: "fas fa-circle-xmark",        // Extraviado
  6: "fas fa-ban",                  // Cancelado
  18: "fas fa-hourglass-end",       // Etiqueta expirada
  10: "fas fa-hourglass-half",      // Pendente
  19: "fas fa-box-open",            // Objeto recebido em nosso CD (Reversa Consolidada)
};

export default {
  name: "ObjectSroJourney",
  props: {
    steps: { type: Array, default: () => [] },
    createdAt: { type: [String, Date], default: "" },
    previousAt: { type: [String, Date], default: "" },
    finish: { type: Boolean, default: false },
    // Sem etapa ativa (espinha toda pendente) o cabeçalho cairia no último passo
    // e anunciaria "Entregue". Quem exibe a espinha apagada informa o rótulo e o
    // ícone do cabeçalho, que então é pintado em cinza.
    headLabel: { type: String, default: "" },
    headIconClass: { type: String, default: "" },
  },
  computed: {
    headStep() {
      return journeyHeadStep(this.steps);
    },
    headTitle() {
      return this.headLabel || this.headStep.title || "";
    },
    headColor() {
      if (this.headLabel) return COLORS.pendingText;
      return COLORS[this.headStep.state] || COLORS.current;
    },
    headIcon() {
      if (this.headIconClass) return this.headIconClass;
      return this.iconForStatus(this.headStep.status);
    },
  },
  methods: {
    iconForStatus(status) {
      return STATUS_ICON[Number(status)] || "fas fa-circle";
    },
    circleStyle(state) {
      const base = {
        width: "38px",
        height: "38px",
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#fff",
        fontSize: "18px",
        boxSizing: "border-box",
        position: "relative",
        zIndex: 1,
      };
      if (state === "done") return { ...base, background: COLORS.done };
      if (state === "current")
        return { ...base, background: COLORS.current, border: "3px solid #FAD2B0" };
      if (state === "warning")
        return { ...base, background: COLORS.warning, border: "3px solid #F5DDB0" };
      if (state === "error")
        return { ...base, background: COLORS.error, border: "3px solid #F8C2C2" };
      return {
        ...base,
        background: "#fff",
        color: COLORS.pendingText,
        border: "1.5px solid " + COLORS.pendingBorder,
      };
    },
    labelColor(state) {
      if (state === "pending") return COLORS.pendingText;
      if (state === "error") return "#B23838";
      return "#303133";
    },
    connectorColor(index) {
      // Cor do segmento pelo nó da DIREITA (o que foi de fato alcançado): assim um nó
      // `pending` após um `done` mantém o conector cinza, sem "linha verde para o nada".
      const right = this.steps[index] || {};
      if (right.state === "error") return COLORS.error;
      if (right.state === "warning") return COLORS.warning;
      if (right.state === "done" || right.state === "current") return COLORS.done;
      return COLORS.track;
    },
    formatShort(date) {
      if (!date || date === "undefined") return "";
      const d = new Date(date);
      if (isNaN(d.getTime())) return "";
      return d.getDate() + " " + MONTHS[d.getMonth()];
    },
    formatCalendarShort(date) {
      return formatShortCalendarDate(date);
    },
    dateFor(index, item) {
      if (index === 0) return this.formatShort(this.createdAt);
      if (index === this.steps.length - 1 && item.state === "pending") {
        const prev = this.formatCalendarShort(this.previousAt);
        return prev ? "Prev. " + prev : "";
      }
      return "";
    },
  },
};
</script>

<style scoped>
.me-journey {
  padding: 8px 4px 4px;
}
.me-journey-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 18px;
}
.me-journey-head i {
  font-size: 20px;
}
.me-journey-title {
  font-size: 16px;
  font-weight: 500;
  color: #303133;
}
.me-journey-track {
  display: flex;
  position: relative;
}
.me-jstep {
  flex: 1;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 7px;
  min-width: 0;
}
.me-jconnector {
  position: absolute;
  top: 17px;
  left: -50%;
  width: 100%;
  height: 3px;
  border-radius: 2px;
  z-index: 0;
}
.me-jlabel {
  font-size: 12px;
  font-weight: 500;
  text-align: center;
  line-height: 1.25;
  padding: 0 2px;
}
.me-jdate {
  font-size: 11px;
  color: #909399;
}
</style>
